import { z } from "zod";
import { AccessDecisionSchema, BillingStatusSchema } from "./billing";

/**
 * Account, session and device contracts (docs/paywall.md). Browser sessions are an HttpOnly
 * cookie; mutating requests carry `X-Charter-CSRF: 1`; the game socket uses a single-use ticket
 * from `POST /v1/ws-ticket`. Non-browser tools authenticate with `Authorization: Bearer <api token>`.
 */

/** Header every cookie-authenticated mutating request must carry (value "1"). */
export const HEADER_CSRF = "x-charter-csrf";

/**
 * Game socket close code: signed in, but this account may not enter the game (no active
 * subscription, banned, refunded...). The client sends the player to the account page.
 */
export const ACCESS_CLOSE_CODE = 4403;

export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 200;
export const EMAIL_MAX = 254;
/** A Discord user ID: a numeric snowflake ("Copy User ID" in Discord's Developer Mode). */
export const DISCORD_USER_ID = /^\d{17,20}$/;

const email = z.string().trim().max(EMAIL_MAX).pipe(z.email());
const password = z.string().min(PASSWORD_MIN).max(PASSWORD_MAX);
const discordUserId = z
  .string()
  .trim()
  .regex(DISCORD_USER_ID, "A Discord user ID is 17-20 digits.");

/** Error codes the account routes answer with (`{ error: { code, message } }`). */
export const AUTH_ERROR_CODES = [
  "bad_request",
  "bad_credentials",
  "login_locked",
  "mfa_required",
  "bad_mfa_code",
  "device_limit",
  "device_removal_limited",
  "weak_password",
  "breached_password",
  "bad_token",
  "email_taken",
  "unauthorized",
  "csrf",
  "forbidden",
  "mfa_enrollment_required",
  "mfa_stale",
  "access_denied",
  "rate_limited",
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

export const SignupBodySchema = z.object({
  email,
  password,
  /** "I am 18 or older". Must be true. */
  ageAttested: z.literal(true),
  /** "I live in the United States". Must be true. */
  usAttested: z.literal(true),
  /** Version of the Terms the player accepted (the site shows it). */
  tosVersion: z.string().trim().min(1).max(40),
  discordUserId: discordUserId.optional(),
  /** Invite link code (volunteer/tester access); handed to billing's signup hook. */
  invite: z.string().trim().min(1).max(100).optional(),
  /** Page to return to after verifying (`accountNext`-checked; e.g. `/waitlist`). */
  next: z.string().max(512).optional(),
});
export type SignupBody = z.infer<typeof SignupBodySchema>;

export const LoginBodySchema = z.object({
  /** Email, or a username for bot / admin / legacy accounts. */
  login: z.string().trim().min(1).max(EMAIL_MAX),
  password: z.string().min(1).max(PASSWORD_MAX),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const EmailOnlyBodySchema = z.object({ email });
export const TokenBodySchema = z.object({ token: z.string().min(10).max(200) });
export const ResetBodySchema = z.object({ token: z.string().min(10).max(200), password });
/** A 6-digit authenticator code or a recovery code (xxxx-xxxx-xx). */
export const MfaCodeBodySchema = z.object({ code: z.string().trim().min(6).max(20) });
export const EmailChangeBodySchema = z.object({
  password: z.string().min(1).max(PASSWORD_MAX),
  newEmail: email,
});
export const PasswordChangeBodySchema = z.object({
  password: z.string().min(1).max(PASSWORD_MAX),
  newPassword: password,
});
/** A user ID, or null to remove it. */
export const DiscordBodySchema = z.object({ discordUserId: discordUserId.nullable() });
/** A Discord link code (docs/paywall.md "Accounts"): 8 Crockford base32 characters. */
export const DISCORD_LINK_CODE = /^[0-9A-HJKMNP-TV-Z]{8}$/;
/** The feedback bot asks a code for a Discord user who said `link` (its storyteller token). */
export const DiscordCodeBodySchema = z.object({
  discordUserId,
  /** The name the bot saw them by: shown when the code links an account (not you? remove it). */
  discordName: z.string().trim().min(1).max(100),
});
/** The signed-in player types in the code the bot sent them. */
export const DiscordRedeemBodySchema = z.object({
  code: z.string().trim().toUpperCase().regex(DISCORD_LINK_CODE),
});
export const PasswordOnlyBodySchema = z.object({ password: z.string().min(1).max(PASSWORD_MAX) });
export const MfaEnableBodySchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/),
});

export const AccountTypeSchema = z.enum(["player", "admin", "bot"]);
export type AccountType = z.infer<typeof AccountTypeSchema>;

/** The signed-in account as the account page and the game see it. Never contains secrets. */
export const AccountViewSchema = z.object({
  id: z.number().int(),
  username: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  accountType: AccountTypeSchema,
  discordUserId: z.string().nullable(),
  /**
   * The player proved the link (typed in the code the bot sent that Discord user: docs/paywall.md
   * "Accounts"). A self-declared link is unverified: the game names no posts and gives no roles by it.
   */
  discordVerified: z.boolean().default(false),
  mfaEnabled: z.boolean(),
  ogNumber: z.number().int().nullable(),
  createdAt: z.string(),
});
export type AccountView = z.infer<typeof AccountViewSchema>;

export const DeviceViewSchema = z.object({
  id: z.number().int(),
  label: z.string(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  region: z.string().nullable(),
  current: z.boolean(),
  /** Unused for `device_stale_days`: removable even inside the removal cooldown. */
  stale: z.boolean(),
});
export type DeviceView = z.infer<typeof DeviceViewSchema>;

export const SessionViewSchema = z.object({
  id: z.number().int(),
  kind: z.enum(["web", "api"]),
  label: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  current: z.boolean(),
});
export type SessionView = z.infer<typeof SessionViewSchema>;

/** GET /v1/me. `character` is the game's character (null before one is created). */
export const MeResponseSchema = z.object({
  account: AccountViewSchema,
  character: z
    .looseObject({
      id: z.number(),
      name: z.string(),
      cls: z.string(),
      level: z.number().optional(),
    })
    .nullable(),
  access: AccessDecisionSchema,
  devices: z.array(DeviceViewSchema),
  maxDevices: z.number().int(),
  /** When the next non-stale device may be removed (null: now). */
  deviceRemovalAvailableAt: z.string().nullable(),
  /** Subscription, entitlement, OG number, waitlist place (same as GET /v1/billing/status). */
  billing: BillingStatusSchema.nullable(),
  /** No real mail on this server (dev): sends only land in the admin Outbox. */
  mailOutbox: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** POST /v1/auth/login. `mfa_required`: post the code to /v1/auth/mfa/verify next. */
export const LoginResponseSchema = z.object({
  ok: z.literal(true),
  mfaRequired: z.boolean(),
  account: AccountViewSchema.optional(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** `GET /v1/auth/session`: always 200 (no 401 noise for signed-out pages). */
export const SessionProbeSchema = z.object({ signedIn: z.boolean(), mfaPending: z.boolean() });
export type SessionProbe = z.infer<typeof SessionProbeSchema>;

export const WsTicketResponseSchema = z.object({ ticket: z.string(), expiresAt: z.string() });
export type WsTicketResponse = z.infer<typeof WsTicketResponseSchema>;

export const MfaSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
});
export const MfaEnableResponseSchema = z.object({ recoveryCodes: z.array(z.string()) });

/**
 * A same-origin path to return to after signing in (`?next=`): starts with one `/`, no scheme,
 * no backslashes or control characters, and one of the allowed prefixes. Anything else → `/play`.
 */
export const SAFE_NEXT_PREFIXES = ["/play", "/account", "/admin", "/waitlist", "/pricing", "/"];
/**
 * Where the account pages (sign-in, sign-up, email verification) may send the browser
 * afterwards: `safeNext`, narrowed to the pages a player signs in for. Anything else → `/account`.
 */
export const ACCOUNT_NEXT_PREFIXES = ["/play", "/account", "/waitlist"] as const;
export const ACCOUNT_DEFAULT_NEXT = "/account";
export function accountNext(raw: string | null | undefined): string {
  const path = safeNext(raw, ACCOUNT_DEFAULT_NEXT);
  const bare = path.split(/[?#]/)[0] ?? "";
  const ok = ACCOUNT_NEXT_PREFIXES.some((p) => bare === p || bare.startsWith(`${p}/`));
  return ok ? path : ACCOUNT_DEFAULT_NEXT;
}

export function safeNext(path: string | null | undefined, fallback = "/play"): string {
  if (typeof path !== "string" || path.length === 0 || path.length > 512) return fallback;
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return fallback;
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return fallback;
  if (/^\/[^/?#]*:/.test(path)) return fallback;
  const bare = path.split(/[?#]/)[0] ?? "";
  const ok = SAFE_NEXT_PREFIXES.some((p) =>
    p === "/" ? bare === "/" : bare === p || bare.startsWith(`${p}/`),
  );
  return ok ? path : fallback;
}
